/**
 * bootLoaderTeardown.js — DEFERRED removal of the pre-React boot loader.
 *
 * WHY (measured bundle main.2f68817b.js, 2026-08-25, headed vY motion pass):
 * a single ~17ms BLANK (#f6f7f9) frame flashed at the BOOT→React loader seam on
 * cold load — reproducible on /gallery/21 and /wheel/2636.
 *
 * MECHANISM: `#iyuga-boot-loader` lives INSIDE `<div id="root">` (see
 * public/index.html + scripts/gen-boot-loader.mjs). React 18's
 * `createRoot(#root).render()` CLEARS #root on its first commit and, in that SAME
 * commit, paints the `<Suspense fallback={<BrandWheelLoader/>}>` fallback (every
 * route page is React.lazy, so the first load suspends). That clear+paint is one
 * browser paint — zero blank frame by itself.
 *
 * The bug was that src/index.js removed the boot loader SYNCHRONOUSLY *before*
 * calling render(). That exposed an intermediate empty #root that the browser
 * could paint ONE frame before React committed → the 1-frame seam flash.
 *
 * FIX: never remove it before render. Schedule removal via DOUBLE
 * requestAnimationFrame *after* render() so the callback runs past a real paint.
 * React's own container-clear already removes the boot loader atomically with
 * the loader paint; this deferred removeChild is belt-and-braces.
 *
 * Double-rAF (not single) because the first rAF callback runs *before* the paint
 * of the frame it is scheduled in. Kept as a tiny pure function so it is
 * unit-testable with a fake rAF (bootLoaderTeardown.test.js — prove-it-can-fail).
 *
 * T2.5 (MEASURED web-stg.iyuga.net main.a1a75c38.js, WebKit 390, invite link ->
 * /login): two frames are NOT a proof that React has committed. createRoot's
 * render() is scheduled, not synchronous, and on a cold load the main thread is
 * still busy with the 9.8 MB bundle — both rAFs fired while #root still held only
 * the boot loader + its sweep <script>, so the teardown removed the only thing on
 * screen ~200 ms before React's first commit (blank gray, then the redirect's
 * empty commit; 15+ flat frames in the recordVideo). The removal now also waits
 * until React has actually put something into the boot loader's container; until
 * then it re-checks every frame. Once React commits it clears the container
 * itself, so in practice this ends as the no-op branch.
 */

export const BOOT_LOADER_ID = 'iyuga-boot-loader';

/**
 * Schedule removal of the boot loader for AFTER React's first paint.
 *
 * @param {object}   [opts]
 * @param {Document} [opts.doc] - document to operate on (default: global document).
 * @param {(cb: FrameRequestCallback) => number} [opts.raf] - rAF impl (injectable
 *   for tests). Falls back to setTimeout(…, 0) where rAF is unavailable (SSR/jsdom).
 * @returns {void}
 */
export function scheduleBootLoaderRemoval(opts = {}) {
  const doc =
    opts.doc || (typeof document !== 'undefined' ? document : undefined);
  if (!doc) return;

  const raf =
    opts.raf ||
    (typeof requestAnimationFrame !== 'undefined'
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 0));

  const removeOnceReactCommitted = () => {
    const el = doc.getElementById(BOOT_LOADER_ID);
    if (!el || !el.parentNode) return; // React's first commit already cleared #root
    if (!hasReactContent(el.parentNode, el)) {
      raf(removeOnceReactCommitted); // React has not committed yet — keep it on screen
      return;
    }
    el.parentNode.removeChild(el);
  };

  // Two nested frames: run only once a committed paint has actually happened.
  raf(() => raf(removeOnceReactCommitted));
}

/**
 * True once the boot loader's container holds an element React rendered — i.e.
 * anything besides the boot loader itself and the inline sweep <script> that
 * public/index.html ships next to it.
 */
function hasReactContent(container, bootLoader) {
  return Array.prototype.some.call(
    container.children,
    (child) => child !== bootLoader && child.tagName !== 'SCRIPT',
  );
}
